import { BabylonNodesService } from '@App/app/engine/services/babylon-nodes-service/babylon-nodes.service';
import {
  DragSideConfig,
  RoiDragConfigService,
} from '@App/app/engine/services/region-of-interest-service/roi-drag-config.service';
import {
  RoiDimensions,
  RoiGeometryService,
  RoiSizeLimits,
} from '@App/app/engine/services/region-of-interest-service/roi-geometry.service';
import {
  AbstractMesh,
  Color3,
  Color4,
  Mesh,
  PointerDragBehavior,
  StandardMaterial,
  Vector3,
} from 'babylonjs';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ELECTRIC_BLUE_COLOR } from '../../configs/babylon.config';
import { Coordinates3 } from '../layer/measurements/coordinates';
import { BoundingVectors } from '../viewer/bounding-vectors.model';
import { CustomScene } from '../viewer/custom-scene.model';
import { registerRoiDragHandlers } from './roi-drag-handlers';

export class RegionOfInterest {
  private _dimensions$ = new BehaviorSubject<RoiDimensions>({
    length: 20,
    height: 20,
    width: 20,
  });
  dimensions$ = this._dimensions$.asObservable();
  root: AbstractMesh | null;
  sides: Mesh[];
  size: number;
  private oldPos: Coordinates3 = { x: 0, y: 0, z: 0 };
  private oldPosMinus: Coordinates3 = { x: 0, y: 0, z: 0 };
  private _isShiftPressed = false;
  private _shiftSubscription: Subscription;
  mat: StandardMaterial;

  constructor(
    size: number,
    scene: CustomScene,
    babylonNodesService: BabylonNodesService,
    public sizeLimits: RoiSizeLimits,
    shiftKeyPressed$: Observable<boolean>,
    private geometryService: RoiGeometryService,
    private dragConfigService: RoiDragConfigService,
  ) {
    this._shiftSubscription = shiftKeyPressed$.subscribe((value) => {
      this._isShiftPressed = value;
    });
    const isShiftPressed = () => this._isShiftPressed;
    this.size = size;
    this.root = new AbstractMesh(RoiNames.root, scene);
    this.mat = new StandardMaterial(RoiNames.mat, scene);
    this.mat.diffuseColor = Color3.FromHexString(ELECTRIC_BLUE_COLOR);
    this.mat.alpha = 0.6;

    this.sides = this.createSidePlanes(size, babylonNodesService);
    this.initializeSidePositions(size);

    const dragBehaviors = this.dragConfigService.buildAllDragBehaviors(
      this.sides,
      sizeLimits,
      isShiftPressed,
    );
    dragBehaviors.forEach(({ config, behavior }) => {
      this.sides[config.sideIndex].addBehavior(behavior);
      const maxSize = this.dragConfigService.getMaxSizeForAxis(config.axis, sizeLimits);
      this.registerDragHandlers(behavior, config, maxSize);
    });
  }

  private createSidePlanes(size: number, babylonNodesService: BabylonNodesService): Mesh[] {
    return Array.from({ length: 6 }, (_, i) => {
      const side = babylonNodesService.createPlane(
        `${RoiNames.side}${i}`,
        { size, updatable: true },
        this.mat,
      );
      side.parent = this.root;
      side.enableEdgesRendering();
      side.edgesWidth = 25.0;
      side.edgesColor = Color4.FromHexString(`${ELECTRIC_BLUE_COLOR}FF`);
      return side;
    });
  }

  private initializeSidePositions(size: number) {
    const halfSize = size / 2;
    const positions = this.geometryService.getSideInitialPositions(halfSize);
    const rotations = this.geometryService.getSideRotations();

    this.sides.forEach((side, i) => {
      side.position = positions[i];
      side.rotation = rotations[i];
    });

    this.oldPos = { x: halfSize, y: halfSize, z: halfSize };
    this.oldPosMinus = { x: -halfSize, y: -halfSize, z: -halfSize };
  }

  private registerDragHandlers(
    behavior: PointerDragBehavior,
    config: DragSideConfig,
    maxSize: number,
  ) {
    registerRoiDragHandlers(
      {
        geometryService: this.geometryService,
        sides: this.sides,
        oldPos: this.oldPos,
        oldPosMinus: this.oldPosMinus,
        size: this.size,
        isShiftPressed: () => this._isShiftPressed,
        emitDimensions: (dimensions) => this._dimensions$.next(dimensions),
      },
      behavior,
      config,
      maxSize,
    );
  }

  setSidesToBoundingVectors(vectors: BoundingVectors) {
    this.sides.forEach((side, index) => {
      const axis = this.geometryService.getSideAxis(index);
      const orientation = this.geometryService.getSideOrientation(index);
      side.position[axis] = vectors[orientation][axis];
      this.geometryService.adjustSidesPositions(
        this.sides,
        index,
        this.geometryService.getCounterSideIndex(index),
        axis,
        this.size,
      );
    });

    this.oldPos = { x: vectors.max.x, y: vectors.max.y, z: vectors.max.z };
    this.oldPosMinus = { x: vectors.min.x, y: vectors.min.y, z: vectors.min.z };
    this._dimensions$.next(this.geometryService.calculateDimensions(this.oldPos, this.oldPosMinus));
  }

  public dispose() {
    this._shiftSubscription.unsubscribe();
    for (const sideElement of this.sides) {
      sideElement.dispose();
    }
    this.sides = [];
    this.root?.dispose();
    this.root = null;
  }

  getCenter() {
    return this.geometryService.calculateCenter(
      new Vector3(this.oldPosMinus.x, this.oldPosMinus.y, this.oldPosMinus.z),
      new Vector3(this.oldPos.x, this.oldPos.y, this.oldPos.z),
    );
  }
}

export enum RoiNames {
  root = 'RegionOfInterest',
  side = 'RegionOfInterestSide',
  mat = 'RegionOfInterestMaterial',
}
